import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import { ScrollFade, Table } from "shared/components";
import { cloneDeep } from "lodash";
import {
  StudentDetailsModal,
  PsiStudentDetailsModal,
  StudentPsiStatus,
  StudentStatus,
} from "../index";
import { ONLINE_REGS_STATUS_INITIALS } from "shared/constants";
import "./ListView.scss";

const isCheckboxAvailable = (reg) =>
  reg.testStatus === ONLINE_REGS_STATUS_INITIALS.active ||
  reg.testStatus === ONLINE_REGS_STATUS_INITIALS.resumed ||
  reg.testStatus === ONLINE_REGS_STATUS_INITIALS.exited;

const mapToList = (
  regs,
  listOfSelectedExaminees,
  toggleExamineeModal,
  scrollPosition,
  isPsiEvent,
  setLastFetchTime,
  seatingChartRequired
) =>
  regs.map((reg) => {
    const hasPendingStatus = reg.status?.includes("pending");
    return {
      name: (
        <button
          className="btn-link examinee-btn"
          onClick={() =>
            toggleExamineeModal(
              isPsiEvent ? reg.regId : reg.id,
              true,
              reg.sectionFormName
            )
          }
        >
          {reg.lastName.trim().concat(", ", reg.firstName.trim())}
        </button>
      ),
      seat: seatingChartRequired
        ? reg.seatRow === null
          ? "-"
          : `${String.fromCharCode("A".charCodeAt(0) + reg.seatRow)}-${
              reg.seatCol + 1
            }`
        : null,
      test: reg.sectionFormName ? reg.sectionFormName : "-",
      status: isPsiEvent ? (
        <StudentPsiStatus
          cellData={reg}
          scrollPosition={scrollPosition}
          className="list-view-status"
          largeIcon={hasPendingStatus}
          setLastFetchTime={setLastFetchTime}
        />
      ) : (
        <StudentStatus
          cellData={reg}
          scrollPosition={scrollPosition}
          className="list-view-status"
          smallIcon
        />
      ),
      hideSelectOption: !isCheckboxAvailable(reg),
      id: reg.id,
      checked: listOfSelectedExaminees.some((el) => el.id === reg.id) || false,
    };
  });

const ListView = (props) => {
  const { t } = useTranslation();
  const {
    onlineRegs,
    listOfSelectedExaminees,
    scrollPosition,
    setScrollPosition,
    rowHeaders,
    isPsiEvent,
    setLastFetchTime,
    seatingChartRequired,
  } = props;

  const TABLE_HEADERS = {
    name: t("onlineTesting.listView.tableHeaders.selectAll"),
    seat: seatingChartRequired
      ? t("onlineTesting.listView.tableHeaders.seat")
      : null,
    status: t("onlineTesting.listView.tableHeaders.status"),
    test: t("onlineTesting.listView.tableHeaders.test"),
  };

  const [currentExamineeId, setCurrentExamineeId] = useState(null);
  const [currentTestName, setCurrentTestName] = useState(null);
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
  const [allSelectedFlag, setSelectAllFlag] = useState(false);
  const [listOfMappedExaminees, setMappedExamineesList] = useState([]);

  const toggleExamineeModal = (examineeId, flag, testName) => {
    setIsOpenDetailsModal(flag);
    setCurrentExamineeId(examineeId || null);
    setCurrentTestName(testName || "-");
  };

  useEffect(() => {
    if (listOfSelectedExaminees.some((reg) => !isCheckboxAvailable(reg))) {
      props.updateExamineesList(listOfSelected);
    } else {
      setMappedExamineesList(
        mapToList(
          onlineRegs,
          listOfSelectedExaminees,
          toggleExamineeModal,
          scrollPosition,
          isPsiEvent,
          setLastFetchTime,
          seatingChartRequired
        )
      );
      updateSelectAllFlag();
    }
  }, [onlineRegs, listOfSelectedExaminees]);

  const selectAllExaminees = (e) => {
    const { checked } = e.target;
    setSelectAllFlag(checked);
    let listOfSelected = cloneDeep(props.listOfSelectedExaminees);
    const availableForSelection = props.onlineRegs.filter((reg) =>
      isCheckboxAvailable(reg)
    );
    if (checked) {
      listOfSelected = [].concat(availableForSelection);
    } else {
      availableForSelection.forEach((el) =>
        listOfSelected.splice(
          listOfSelected.findIndex((reg) => reg.id === el.id),
          1
        )
      );
    }
    props.updateExamineesList(listOfSelected);
  };

  const selectExaminee = (e) => {
    const { checked, value } = e.target;
    const listOfSelected = cloneDeep(props.listOfSelectedExaminees);

    const idx = onlineRegs.findIndex((exam) => exam.id === +value);
    if (checked) {
      listOfSelected.push(onlineRegs[idx]);
    } else {
      listOfSelected.splice(
        listOfSelected.findIndex((el) => el.id === +value),
        1
      );
    }
    updateSelectAllFlag();
    props.updateExamineesList(listOfSelected);
  };

  const updateSelectAllFlag = () => {
    if (listOfSelectedExaminees.length === 0) {
      setSelectAllFlag(false);
    } else {
      const flagUnchecked = onlineRegs
        .filter((el) => isCheckboxAvailable(el))
        .some(
          (reg) =>
            !listOfSelectedExaminees.find((regSel) => regSel.id === reg.id)
        );
      setSelectAllFlag(!flagUnchecked);
    }
  };

  return (
    <div className="list-view-container">
      <ScrollFade
        setScrollPosition={setScrollPosition}
        scrollPosition={scrollPosition}
      >
        <Table
          allSelected={allSelectedFlag}
          onSelectAll={selectAllExaminees}
          onSelectRow={selectExaminee}
          rowElements={rowHeaders}
          tableHeaders={TABLE_HEADERS}
          listOfRegs={listOfMappedExaminees}
          hideSelectAll={!onlineRegs.some((reg) => isCheckboxAvailable(reg))}
          selectAllOptionEnabled
          selectOptionEnabled
          isPsiEvent
        ></Table>
      </ScrollFade>
      {isPsiEvent ? (
        <PsiStudentDetailsModal
          currentTestName={currentTestName}
          id={currentExamineeId}
          isOpen={isOpenDetailsModal}
          handleClose={() => toggleExamineeModal(null, false)}
        />
      ) : (
        <StudentDetailsModal
          id={currentExamineeId}
          isOpen={isOpenDetailsModal}
          handleClose={() => toggleExamineeModal(null, false)}
        />
      )}
    </div>
  );
};

ListView.propTypes = {
  onlineRegs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  listOfSelectedExaminees: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
ListView.defaultProps = {};

export default ListView;
